import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { fmtDate } from '../../lib/constants';
import Modal from '../../components/ui/Modal';

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

const GROUP_COLORS = [
  '#D32F2F','#1565C0','#2E7D32','#F57F17','#6A1B9A',
  '#00838F','#AD1457','#4527A0','#558B2F','#E64A19',
  '#0277BD','#37474F',
];

export default function CalendarPage() {
  const today    = new Date();
  const [year,   setYear]   = useState(today.getFullYear());
  const [month,  setMonth]  = useState(today.getMonth()); // 0-indexed
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState({});
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null); // clicked event
  const [view,   setView]   = useState('month'); // 'month' | 'list'

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    const [{ data: evts }, { data: grps }] = await Promise.all([
      supabase.from('group_events')
        .select('*, groups(id, name)')
        .order('date', { ascending: true }),
      supabase.from('groups').select('id, name'),
    ]);

    // Build color map per group
    const colorMap = {};
    (grps || []).forEach((g, i) => { colorMap[g.id] = GROUP_COLORS[i % GROUP_COLORS.length]; });
    setGroups(colorMap);
    setEvents(evts || []);
  }

  // ── helpers ────────────────────────────────────────────────
  function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
  function firstDayOfMonth(y, m) { return new Date(y, m, 1).getDay(); } // 0=Sun

  function eventsForDay(day) {
    const dateStr = `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return events.filter(e => e.date === dateStr && (filter === 'all' || e.groups?.id === filter));
  }

  function eventsForMonth() {
    const prefix = `${year}-${String(month + 1).padStart(2,'0')}`;
    return events.filter(e => e.date?.startsWith(prefix) && (filter === 'all' || e.groups?.id === filter))
                 .sort((a, b) => a.date.localeCompare(b.date));
  }

  function allUpcoming() {
    const today = new Date().toISOString().split('T')[0];
    return events.filter(e => e.date >= today && (filter === 'all' || e.groups?.id === filter))
                 .sort((a, b) => a.date.localeCompare(b.date));
  }

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  }

  const totalDays   = daysInMonth(year, month);
  const startOffset = firstDayOfMonth(year, month);
  const cells       = Array.from({ length: startOffset + totalDays }, (_, i) =>
    i < startOffset ? null : i - startOffset + 1
  );

  // Unique groups list for filter
  const groupList = Object.entries(
    events.reduce((acc, e) => {
      if (e.groups) acc[e.groups.id] = e.groups.name;
      return acc;
    }, {})
  );

  return (
    <div>
      {/* Header */}
      <div className="flex-between mb-4">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800 }}>📅 Church Calendar</h1>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>
            All events from all groups combined
          </div>
        </div>
        <div className="flex gap-2">
          <button className={`btn btn-sm ${view === 'month' ? 'btn-primary' : 'btn-ghost'}`}
            style={{ border: view !== 'month' ? '1.5px solid var(--border)' : '' }}
            onClick={() => setView('month')}>🗓 Month</button>
          <button className={`btn btn-sm ${view === 'list' ? 'btn-primary' : 'btn-ghost'}`}
            style={{ border: view !== 'list' ? '1.5px solid var(--border)' : '' }}
            onClick={() => setView('list')}>📋 List</button>
        </div>
      </div>

      {/* Group filter */}
      <div className="card mb-4">
        <div className="card-pad" style={{ paddingTop: 12, paddingBottom: 12 }}>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setFilter('all')}
              className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-ghost'}`}
              style={{ border: filter !== 'all' ? '1.5px solid var(--border)' : '' }}>
              All Groups
            </button>
            {groupList.map(([id, name]) => (
              <button key={id} onClick={() => setFilter(id)}
                className="btn btn-sm"
                style={{
                  background: filter === id ? groups[id] : 'white',
                  color:      filter === id ? 'white' : 'var(--text2)',
                  border:     `1.5px solid ${groups[id] || 'var(--border)'}`,
                  fontWeight: 600,
                }}>
                {name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Month navigation */}
      <div className="flex-between mb-3">
        <button className="btn btn-ghost" onClick={prevMonth}>← Prev</button>
        <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: 18 }}>
          {MONTHS[month]} {year}
        </div>
        <button className="btn btn-ghost" onClick={nextMonth}>Next →</button>
      </div>

      {/* ── MONTH VIEW ── */}
      {view === 'month' && (
        <div className="card">
          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: 'var(--red-dark)', borderRadius: '12px 12px 0 0' }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} style={{ padding: '10px 4px', textAlign: 'center', color: 'white', fontSize: 11, fontWeight: 700, letterSpacing: .5 }}>{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
            {cells.map((day, idx) => {
              const dayEvents = day ? eventsForDay(day) : [];
              const isToday   = day && year === today.getFullYear() && month === today.getMonth() && day === today.getDate();
              return (
                <div key={idx} style={{
                  minHeight: 90,
                  padding: '6px 5px',
                  borderRight: (idx + 1) % 7 === 0 ? 'none' : '1px solid var(--border)',
                  borderBottom: '1px solid var(--border)',
                  background: !day ? 'var(--bg)' : 'white',
                  verticalAlign: 'top',
                }}>
                  {day && (
                    <>
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%',
                        background: isToday ? 'var(--red)' : 'transparent',
                        color: isToday ? 'white' : 'var(--text)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: isToday ? 700 : 500, marginBottom: 4,
                      }}>
                        {day}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {dayEvents.slice(0, 3).map(e => (
                          <div key={e.id}
                            onClick={() => setDetail(e)}
                            title={e.title}
                            style={{
                              background: groups[e.groups?.id] || 'var(--blue)',
                              color: 'white', fontSize: 10, fontWeight: 600,
                              padding: '2px 5px', borderRadius: 4, cursor: 'pointer',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              lineHeight: 1.5,
                            }}>
                            {e.title}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600 }}>
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {groupList.map(([id, name]) => (
              <div key={id} className="flex-center gap-1" style={{ fontSize: 11, color: 'var(--text2)' }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: groups[id], flexShrink: 0 }} />
                {name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <div className="card">
          <div className="card-header">
            <h3>All Upcoming Events</h3>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>{allUpcoming().length} events</span>
          </div>
          {allUpcoming().length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>No upcoming events.</div>
          ) : (
            allUpcoming().map((e, i) => {
              const color = groups[e.groups?.id] || 'var(--blue)';
              return (
                <div key={e.id} onClick={() => setDetail(e)} style={{
                  display: 'flex', gap: 14, padding: '14px 18px',
                  borderBottom: '1px solid var(--border)', cursor: 'pointer',
                  borderLeft: `4px solid ${color}`,
                  background: 'white',
                }} onMouseEnter={el => el.currentTarget.style.background = 'var(--bg)'}
                   onMouseLeave={el => el.currentTarget.style.background = 'white'}>
                  <div style={{
                    background: color, borderRadius: 8, padding: '8px 12px',
                    textAlign: 'center', flexShrink: 0, minWidth: 48,
                  }}>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10, fontWeight: 700 }}>
                      {new Date(e.date).toLocaleString('default',{month:'short'}).toUpperCase()}
                    </div>
                    <div style={{ color: 'white', fontSize: 20, fontWeight: 900, lineHeight: 1.1 }}>
                      {new Date(e.date).getDate()}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{e.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                      <span style={{ background: color + '22', color, padding: '1px 7px', borderRadius: 100, fontWeight: 700, fontSize: 11 }}>
                        {e.groups?.name}
                      </span>
                      {e.time && <span style={{ marginLeft: 8 }}>🕐 {e.time}</span>}
                      {e.location && <span style={{ marginLeft: 8 }}>📍 {e.location}</span>}
                    </div>
                    {e.description && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>{e.description}</div>}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Event detail modal */}
      {detail && (
        <Modal title={detail.title} onClose={() => setDetail(null)}
          footer={<button className="btn btn-ghost" onClick={() => setDetail(null)}>Close</button>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <span className="badge" style={{ background: (groups[detail.groups?.id] || 'var(--blue)') + '22', color: groups[detail.groups?.id] || 'var(--blue)' }}>
                {detail.groups?.name}
              </span>
            </div>
            {[
              ['📅 Date',     fmtDate(detail.date)],
              ['🕐 Time',     detail.time   || '—'],
              ['📍 Location', detail.location|| '—'],
            ].map(([l,v]) => (
              <div key={l} style={{ display: 'flex', gap: 12 }}>
                <span style={{ fontSize: 13, color: 'var(--text3)', width: 100, flexShrink: 0 }}>{l}</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{v}</span>
              </div>
            ))}
            {detail.description && (
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px', fontSize: 13, lineHeight: 1.7 }}>
                {detail.description}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
